import React, { useCallback, useEffect, useState } from "react";
import "./StoryViewer.css";

// Instagram-story-style fullscreen viewer. Renders one slide at a
// time inside a centered story column: segmented progress bar on top,
// X in the top-right, and tap zones — left third goes back, right
// two-thirds advances (advancing past the last slide closes, same as
// stories). Arrow keys / Escape work too. Slides are pre-rendered
// nodes so the same viewer serves both photo slides and leader
// profile slides.
//
// Props:
//   - slides: [{ key, node }]
//   - onClose: () => void
//   - startIndex: optional initial slide

const StoryViewer = ({ slides, onClose, startIndex = 0 }) => {
  const count = slides.length;
  const [index, setIndex] = useState(
    Math.min(Math.max(startIndex, 0), Math.max(count - 1, 0))
  );

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i >= count - 1) {
        onClose();
        return i;
      }
      return i + 1;
    });
  }, [count, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Lock page scroll while the story is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (count === 0) return null;
  const current = slides[index];

  return (
    <div className="story-viewer" role="dialog" aria-modal="true">
      <div className="story-viewer__column">
        <div className="story-viewer__progress" aria-hidden="true">
          {slides.map((s, i) => (
            <span
              key={s.key || i}
              className={`story-viewer__segment ${
                i < index
                  ? "story-viewer__segment--done"
                  : i === index
                  ? "story-viewer__segment--active"
                  : ""
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          className="story-viewer__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="story-viewer__slide">{current.node}</div>

        <button
          type="button"
          className="story-viewer__zone story-viewer__zone--prev"
          onClick={goPrev}
          aria-label="Previous"
          tabIndex={-1}
        >
          {index > 0 && <span className="story-viewer__chevron">‹</span>}
        </button>
        <button
          type="button"
          className="story-viewer__zone story-viewer__zone--next"
          onClick={goNext}
          aria-label="Next"
          tabIndex={-1}
        >
          <span className="story-viewer__chevron">›</span>
        </button>
      </div>
    </div>
  );
};

export default StoryViewer;
